import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { calculateSampleSize } from '../../../utils/statistics';
import { FormState, CalculationResult } from '../types';

const FIXED_MDE_VALUES = [2, 5, 10, 15, 20, 25];

interface MDEComparisonTableProps {
  formState: FormState;
  selectedMDE: number;
}

function formatPercentChange(value: number): string {
  return value > 0 ? `+${value}%` : `${value}%`;
}

function formatNumber(num: number): string {
  return num.toLocaleString();
}

export const MDEComparisonTable: React.FC<MDEComparisonTableProps> = ({ formState, selectedMDE }) => {
  const [open, setOpen] = useState(true);

  // Build sorted unique MDE list
  const mdeValues = Array.from(
    new Set([...FIXED_MDE_VALUES, selectedMDE])
  ).sort((a, b) => a - b);

  // Calculate result for each MDE
  const results = mdeValues.map((mde) => {
    const result: CalculationResult = calculateSampleSize({
      ...formState,
      mde,
    });
    return {
      mde,
      ...result,
    };
  });

  // Find the result for the selected MDE
  const selectedResult = results.find((r) => r.mde === selectedMDE);
  const selectedSampleSize = selectedResult?.totalSampleSize || 1;

  // Compute percent change vs selected MDE
  const resultsWithChange = results.map((r) => {
    if (r.mde === selectedMDE) return { ...r, percentChange: null };
    const percentChange = Math.round(((r.totalSampleSize - selectedSampleSize) / selectedSampleSize) * 100);
    return { ...r, percentChange };
  });

  return (
    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 mt-2">
      <button
        type="button"
        className="w-full flex items-center justify-between mb-2 px-0 py-0 bg-transparent border-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded transition cursor-pointer"
        aria-expanded={open}
        aria-controls="mde-comparison-table-section"
        onClick={() => setOpen((open) => !open)}
      >
        <h3 className="font-medium m-0 text-left">Compare Sample Size by MDE</h3>
        {open ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-2" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
        )}
        <span className="sr-only">{open ? 'Hide' : 'Show'} MDE comparison</span>
      </button>
      {open && (
        <div id="mde-comparison-table-section">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            See how different Minimum Detectable Effect (MDE) values would affect your sample size requirements, keeping all other parameters constant.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="py-1 px-2 text-left">MDE</th>
                  <th className="py-1 px-2 text-left">Total Sample Size</th>
                  <th className="py-1 px-2 text-left">Estimated Days</th>
                </tr>
              </thead>
              <tbody>
                {resultsWithChange.map((row) => {
                  const isSelected = row.mde === selectedMDE;
                  return (
                    <tr
                      key={row.mde}
                      className={
                        isSelected
                          ? 'font-bold bg-blue-100 dark:bg-blue-900/40'
                          : 'bg-transparent'
                      }
                    >
                      <td className="py-1 px-2">{isSelected ? <b>{row.mde}%</b> : `${row.mde}%`}</td>
                      <td className="py-1 px-2">
                        {isSelected ? (
                          <b>{formatNumber(row.totalSampleSize)}</b>
                        ) : (
                          <>
                            {formatNumber(row.totalSampleSize)}{' '}
                            <span className="text-gray-500 dark:text-gray-400">({formatPercentChange(row.percentChange!)})</span>
                          </>
                        )}
                      </td>
                      <td className="py-1 px-2">{isSelected ? <b>{formatNumber(row.daysToRun)}</b> : formatNumber(row.daysToRun)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MDEComparisonTable;
